import type { Ref } from 'vue';
import { computed } from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import type { ITableDataResponse, ITableState } from '@modules/tables/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { keepPreviousData } from '@tanstack/vue-query';

import type { IPostbacksListItem } from '../types';
import {
  ADMIN_POSTBACKS_LIST_URL,
  AFFILIATE_POSTBACKS_LIST_URL,
  POSTBACKS_LIST_QUERY_KEY,
  POSTBACKS_PERMISSIONS,
} from '../constants';

export default function usePostbacksListQuery(
  tableState: Ref<ITableState>,
  type: Ref<string>,
  isDisabled: Ref<boolean>,
  affiliateId: Ref<number | undefined>,
): IContextQueryResult<ITableDataResponse<IPostbacksListItem>> {
  const { fetchPost } = useRestClient();
  const { isAdminNamespace } = useCurrentNamespace();

  return useContextQuery<ITableDataResponse<IPostbacksListItem>>({
    queryKey: [POSTBACKS_LIST_QUERY_KEY, tableState, type, affiliateId],
    queryFn: ({ signal }) => fetchPost<ITableDataResponse<IPostbacksListItem>>(
      isAdminNamespace.value ? ADMIN_POSTBACKS_LIST_URL : AFFILIATE_POSTBACKS_LIST_URL,
      {
        ...tableState.value,
        type: type.value,
      },
      {
        signal,
        permission: POSTBACKS_PERMISSIONS.SHOW,
        urlParams: {
          id: affiliateId?.value,
        },
      },
    ),
    enabled: computed(() => !isDisabled.value && !!type.value),
    placeholderData: keepPreviousData,
  });
}
